/**
 * Licensing API Client
 *
 * Provides frontend access to the licensing service endpoints.
 * Part of Issue #220: Tenant Settings Consolidation & Licensing Strategy
 */

import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  LicenseTier,
  LicenseModule,
  TenantLicense,
  LicenseHistoryEntry,
  LicenseTiersResponse,
  LicenseModulesResponse,
  LicenseHistoryResponse,
  AssignLicenseRequest,
  GrantModuleRequest,
  RevokeModuleRequest,
  TenantModulesResponse,
} from '@internal/plugin-tenant-management-common';
import { generateCorrelationId } from '../utils/errorUtils';

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function invalidLicensingResponse(fieldName: string): Error {
  return new Error(
    `Invalid Licensing API response: missing or malformed '${fieldName}'`,
  );
}

function requireLicenseTiers(payload: unknown): LicenseTier[] {
  if (!isJsonObject(payload) || !Array.isArray(payload.tiers)) {
    throw invalidLicensingResponse('tiers');
  }

  return payload.tiers as LicenseTier[];
}

function requireLicenseModules(payload: unknown): LicenseModule[] {
  if (!isJsonObject(payload) || !Array.isArray(payload.modules)) {
    throw invalidLicensingResponse('modules');
  }

  return payload.modules as LicenseModule[];
}

function requireLicenseHistory(payload: unknown): LicenseHistoryEntry[] {
  if (!isJsonObject(payload) || !Array.isArray(payload.data)) {
    throw invalidLicensingResponse('data');
  }

  return payload.data as LicenseHistoryEntry[];
}

/**
 * Default implementation of the licensing API
 */
export class LicensingApiClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('tenant-management');
  }

  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'X-Correlation-Id': generateCorrelationId(),
    };
  }

  /**
   * Helper to throw ResponseError from a failed response.
   * Uses Backstage's ResponseError for consistent error handling.
   */
  private async throwResponseError(response: Response): Promise<never> {
    throw await ResponseError.fromResponse(response);
  }

  async getTiers(): Promise<LicenseTier[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/licensing/tiers`, {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    const data = (await response.json()) as LicenseTiersResponse;
    return requireLicenseTiers(data);
  }

  async getModules(): Promise<LicenseModule[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/licensing/modules`, {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    const data = (await response.json()) as LicenseModulesResponse;
    return requireLicenseModules(data);
  }

  async getTenantLicense(tenantId: string): Promise<TenantLicense | null> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/license`,
      { headers },
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return (await response.json()) as TenantLicense;
  }

  async assignLicense(
    tenantId: string,
    request: AssignLicenseRequest,
  ): Promise<TenantLicense> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/license`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return (await response.json()) as TenantLicense;
  }

  async getLicenseHistory(
    tenantId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<LicenseHistoryEntry[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const params = new URLSearchParams();
    if (options.limit !== undefined)
      params.set('limit', options.limit.toString());
    if (options.offset !== undefined)
      params.set('offset', options.offset.toString());

    const queryString = params.toString();
    const url = `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/license/history${queryString ? `?${queryString}` : ''}`;

    const response = await this.fetchApi.fetch(url, { headers });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    // Backend returns PaginatedResponse with data array (Issue #12)
    const result = (await response.json()) as LicenseHistoryResponse;
    return requireLicenseHistory(result);
  }

  async getTenantModules(tenantId: string): Promise<TenantModulesResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/modules`,
      { headers },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return (await response.json()) as TenantModulesResponse;
  }

  async grantModule(
    tenantId: string,
    moduleId: string,
    request: GrantModuleRequest,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/modules/${encodeURIComponent(moduleId)}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }
  }

  async revokeModule(
    tenantId: string,
    moduleId: string,
    request: RevokeModuleRequest = {},
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/modules/${encodeURIComponent(moduleId)}`,
      {
        method: 'DELETE',
        headers,
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }
  }
}

type LicensingApiMethod =
  | 'getTiers'
  | 'getModules'
  | 'getTenantLicense'
  | 'assignLicense'
  | 'getLicenseHistory'
  | 'getTenantModules'
  | 'grantModule'
  | 'revokeModule';

/**
 * API interface for licensing operations.
 *
 * The type is derived from the concrete client so the public Backstage API
 * contract stays in sync with the implementation without duplicating method
 * parameter declarations in a type-only interface.
 */
export type LicensingApi = Pick<LicensingApiClient, LicensingApiMethod>;

/**
 * API reference for licensing
 */
export const licensingApiRef = createApiRef<LicensingApi>().with({
  id: 'plugin.tenant-management.licensing',
});
